import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, FileDown, Plus, Trash2, X } from 'lucide-react'

import { useAppStore } from '@core/store'
import { downloadBlob } from '@core/lib/downloadBlob'
import { viewerApi } from '@viewer/lib/viewerApi'
import { compor } from '@viewer/report/buildBlocks'
import { recolherDados } from '@viewer/report/collectData'
import {
  MARGEM_MM,
  PAPEL_MM,
  type Papel,
  RODAPE_MM,
  paginar,
} from '@viewer/report/documentLayout'
import {
  type BlocoDocumento,
  type DadosDocumento,
  type Documento,
  type TipoBloco,
  dadosVazios,
  documentoInicial,
  gravarDocumentos,
  LIGACOES,
  ligacoesDesconhecidas,
  lerDocumentos,
  resolverTexto,
} from '@viewer/report/documentModel'
import { documentoParaPdf } from '@viewer/report/documentToPdf'

const TIPOS: { valor: TipoBloco; label: string }[] = [
  { valor: 'titulo', label: 'Título' },
  { valor: 'subtitulo', label: 'Subtítulo' },
  { valor: 'paragrafo', label: 'Parágrafo' },
  { valor: 'tabela-classes', label: 'Tabela de classes' },
  { valor: 'topicos-bcf', label: 'Tópicos BCF' },
  { valor: 'quebra', label: 'Quebra de página' },
]

const SEM_TEXTO: TipoBloco[] = ['tabela-classes', 'topicos-bcf', 'quebra']

/** O que o recolector precisa de um modelo carregado. */
type ModeloDoViewer = Parameters<typeof recolherDados>[1] extends (
  id: string,
) => infer R
  ? NonNullable<R>
  : never

/** O Documento: uma página sobre o modelo, impressa em PDF.
 *
 *  Guarda ligações e não valores: o mesmo documento aberto sobre a
 *  revisão seguinte do IFC lê a revisão seguinte. */
export default function ReportPanel({ aoFechar }: { aoFechar: () => void }) {
  const models = useAppStore((s) => s.models)
  const bcfPinTopics = useAppStore((s) => s.bcfPinTopics)

  const [documento, setDocumento] = useState<Documento>(documentoInicial)
  const [guardados, setGuardados] = useState<Documento[]>(lerDocumentos)
  const [dados, setDados] = useState<DadosDocumento>(dadosVazios)
  const [papel, setPapel] = useState<Papel>('A4')
  const [aLer, setALer] = useState(false)
  const [mensagem, setMensagem] = useState<string | null>(null)

  const lerModelo = async (): Promise<DadosDocumento> => {
    setALer(true)
    try {
      const d = await recolherDados(
        models.map((m) => ({
          id: m.id,
          fileName: m.fileName,
          schemaVersion: m.schemaVersion,
          entityCount: m.entityCount,
        })),
        // O modelo do Fragments tem os dois métodos que o recolector
        // precisa; o molde diz isso sem arrastar o tipo inteiro.
        (id) => viewerApi.getModel(id) as unknown as ModeloDoViewer | null,
        bcfPinTopics,
      )
      setDados(d)
      return d
    } finally {
      setALer(false)
    }
  }

  const paginas = useMemo(() => {
    const { largura, altura } = PAPEL_MM[papel]
    const larguraUtil = largura - MARGEM_MM * 2
    const alturaUtil = altura - MARGEM_MM * 2 - RODAPE_MM
    return paginar(compor(documento, dados, larguraUtil), alturaUtil)
  }, [documento, dados, papel])

  const exportar = async () => {
    const d = models.length > 0 ? await lerModelo() : dados
    const { largura, altura } = PAPEL_MM[papel]
    const pags = paginar(
      compor(documento, d, largura - MARGEM_MM * 2),
      altura - MARGEM_MM * 2 - RODAPE_MM,
    )
    const bytes = documentoParaPdf(pags, {
      papel,
      rodape: `${documento.nome} · VN2R openBIM Hub`,
    })
    downloadBlob(
      new Blob([bytes as BlobPart], { type: 'application/pdf' }),
      `${documento.nome.replace(/[^\w\-. ]+/g, '_')}.pdf`,
    )
    setMensagem(`PDF gerado com ${pags.length} ${pags.length === 1 ? 'página' : 'páginas'}.`)
  }

  const mudarBloco = (id: string, mudanca: Partial<BlocoDocumento>) => {
    setDocumento((d) => ({
      ...d,
      blocos: d.blocos.map((b) => (b.id === id ? { ...b, ...mudanca } : b)),
    }))
  }

  const moverBloco = (id: string, direcao: -1 | 1) => {
    setDocumento((d) => {
      const i = d.blocos.findIndex((b) => b.id === id)
      const j = i + direcao
      if (i < 0 || j < 0 || j >= d.blocos.length) return d
      const blocos = [...d.blocos]
      ;[blocos[i], blocos[j]] = [blocos[j], blocos[i]]
      return { ...d, blocos }
    })
  }

  const guardar = () => {
    const lista = [...guardados.filter((g) => g.nome !== documento.nome), documento]
    setGuardados(lista)
    gravarDocumentos(lista)
    setMensagem('Documento guardado neste browser.')
  }

  return (
    <div className="flex h-full flex-col gap-2 overflow-y-auto p-2 text-xs">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[var(--color-text)]">
          Documento
        </span>
        <button
          type="button"
          onClick={aoFechar}
          aria-label="Fechar o documento"
          className="rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      <p className="text-[10px] leading-relaxed text-[var(--color-text-dim)]">
        Uma página sobre o modelo, impressa em PDF. Guarda as ligações e
        não os valores: o mesmo documento aberto sobre a revisão seguinte
        lê a revisão seguinte.
      </p>

      <input
        value={documento.nome}
        onChange={(e) => setDocumento((d) => ({ ...d, nome: e.target.value }))}
        aria-label="Nome do documento"
        className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1.5 py-1"
      />

      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          onClick={() => void lerModelo()}
          disabled={models.length === 0 || aLer}
          className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text)] hover:bg-[var(--color-surface)] disabled:opacity-50"
        >
          {aLer ? 'A ler o modelo…' : 'Actualizar dados'}
        </button>
        <select
          value={papel}
          onChange={(e) => setPapel(e.target.value as Papel)}
          aria-label="Tamanho do papel"
          className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-1"
        >
          <option value="A4">A4</option>
          <option value="A3">A3</option>
        </select>
        <button
          type="button"
          onClick={() => void exportar()}
          className="flex items-center gap-1 rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-1 font-medium text-[var(--color-accent)]"
        >
          <FileDown className="h-3 w-3" aria-hidden /> PDF
        </button>
        <button
          type="button"
          onClick={guardar}
          className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
        >
          Guardar
        </button>
      </div>

      {guardados.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-[10px] text-[var(--color-text-dim)]">
            Guardados:
          </span>
          {guardados.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setDocumento(g)}
              className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
            >
              {g.nome}
            </button>
          ))}
        </div>
      )}

      {mensagem && (
        <p className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-dim)]">
          {mensagem}
        </p>
      )}

      <ul className="space-y-1">
        {documento.blocos.map((b) => {
          const desconhecidas = ligacoesDesconhecidas(b.texto)
          return (
            <li
              key={b.id}
              className="rounded border border-[var(--color-border)] p-1.5"
            >
              <div className="flex items-center gap-1">
                <select
                  value={b.tipo}
                  onChange={(e) =>
                    mudarBloco(b.id, { tipo: e.target.value as TipoBloco })
                  }
                  aria-label="Tipo de bloco"
                  className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-0.5"
                >
                  {TIPOS.map((t) => (
                    <option key={t.valor} value={t.valor}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label="Subir o bloco"
                  onClick={() => moverBloco(b.id, -1)}
                  className="rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
                >
                  <ArrowUp className="h-3 w-3" aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label="Descer o bloco"
                  onClick={() => moverBloco(b.id, 1)}
                  className="rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
                >
                  <ArrowDown className="h-3 w-3" aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label="Apagar o bloco"
                  onClick={() =>
                    setDocumento((d) => ({
                      ...d,
                      blocos: d.blocos.filter((x) => x.id !== b.id),
                    }))
                  }
                  className="ml-auto rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-error-text)]"
                >
                  <Trash2 className="h-3 w-3" aria-hidden />
                </button>
              </div>
              {!SEM_TEXTO.includes(b.tipo) && (
                <>
                  <textarea
                    value={b.texto}
                    onChange={(e) => mudarBloco(b.id, { texto: e.target.value })}
                    rows={b.tipo === 'paragrafo' ? 3 : 1}
                    aria-label="Texto do bloco"
                    className="mt-1 w-full resize-y rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-0.5"
                  />
                  {desconhecidas.length > 0 && (
                    <p className="text-[var(--color-warn-text)]">
                      Ligações que não existem: {desconhecidas.join(', ')}
                    </p>
                  )}
                  <p className="truncate text-[10px] text-[var(--color-text-dim)]">
                    {resolverTexto(b.texto, dados)}
                  </p>
                </>
              )}
            </li>
          )
        })}
      </ul>

      <button
        type="button"
        onClick={() =>
          setDocumento((d) => ({
            ...d,
            blocos: [
              ...d.blocos,
              { id: `b-${Date.now()}`, tipo: 'paragrafo', texto: '' },
            ],
          }))
        }
        className="flex items-center gap-1 self-start rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
      >
        <Plus className="h-3 w-3" aria-hidden /> Bloco
      </button>

      <details className="border-t border-[var(--color-border)] pt-1">
        <summary className="cursor-pointer text-[10px] text-[var(--color-text-dim)]">
          Ligações disponíveis
        </summary>
        <ul className="mt-1 space-y-0.5">
          {LIGACOES.map((l) => (
            <li key={l.chave} className="flex gap-2 text-[10px]">
              <code className="shrink-0 text-[var(--color-accent)]">
                {`{${l.chave}}`}
              </code>
              <span className="text-[var(--color-text-dim)]">{l.descricao}</span>
            </li>
          ))}
        </ul>
      </details>

      <p className="text-[10px] text-[var(--color-text-dim)]">
        {paginas.length} {paginas.length === 1 ? 'página' : 'páginas'} em{' '}
        {papel}.
      </p>
    </div>
  )
}
